/**
 * The oRPC **contract**: every Wave-0 procedure with its input schema, output schema
 * and OpenAPI route metadata. There is no handler here — the implementation lives in
 * `apps/api` and imports this contract, so clients never pull server code.
 *
 * Keeping the procedure definitions in this package is also the escape hatch the
 * platform audit asked for: if oRPC's 1.x risk ever becomes unacceptable, swapping to
 * `@hono/zod-openapi` touches one adapter file in `apps/api`, not the clients.
 *
 * Conventions enforced here:
 * - Every procedure declares `method` + `path` + a stable `operationId`, because
 *   `openapi.json` is the input to `swift-openapi-generator` and a churning
 *   operationId silently renames Swift methods.
 * - Path params must be **required** properties of the input object; oRPC's compact
 *   input structure extracts them by name and throws at generation time otherwise.
 * - `Idempotency-Key`, `X-Client` and `X-Correlation-Id` are transport headers
 *   validated by middleware (see `requestHeadersSchema`), not procedure inputs.
 */

import { type HTTPMethod, type HTTPPath, oc } from '@orpc/contract';
import {
  chatActionInputSchema,
  chatActionOutputSchema,
  chatHistoryInputSchema,
  chatHistoryOutputSchema,
  chatSendInputSchema,
  chatSendOutputSchema,
  chatSessionsInputSchema,
  chatSessionsOutputSchema,
} from './schemas/chat.ts';
import {
  commitmentConfirmInputSchema,
  commitmentSchema,
  commitmentsListInputSchema,
  commitmentsListOutputSchema,
  commitmentUpdateInputSchema,
} from './schemas/commitments.ts';
import { appErrorSchema, okSchema, paginated } from './schemas/common.ts';
import {
  contactCreateInputSchema,
  contactDeleteInputSchema,
  contactDeleteOutputSchema,
  contactDetailSchema,
  contactEmailAddInputSchema,
  contactEmailRemoveInputSchema,
  contactEmailSchema,
  contactGetInputSchema,
  contactListInputSchema,
  contactListOutputSchema,
  contactMatchKeysInputSchema,
  contactMatchKeysOutputSchema,
  contactPhoneAddInputSchema,
  contactPhoneRemoveInputSchema,
  contactPhoneSchema,
  contactRestoreInputSchema,
  contactSchema,
  contactUpdateInputSchema,
} from './schemas/contacts.ts';
import {
  deviceCreatePairingCodeInputSchema,
  deviceListOutputSchema,
  devicePairingCodeSchema,
  devicePushTokenSchema,
  devicePushTokenUpdateInputSchema,
  deviceRevokeInputSchema,
  deviceSchema,
} from './schemas/devices.ts';
import {
  enrichmentApplyIdentityInputSchema,
  enrichmentApplyIdentityOutputSchema,
  enrichmentBudgetInputSchema,
  enrichmentBudgetOutputSchema,
  enrichmentCandidateDecisionInputSchema,
  enrichmentCandidateDecisionOutputSchema,
  enrichmentCandidateRejectInputSchema,
  enrichmentCandidatesListInputSchema,
  enrichmentCandidatesListOutputSchema,
  enrichmentHistoryInputSchema,
  enrichmentHistoryOutputSchema,
  enrichmentIdentifyInputSchema,
  enrichmentIdentifyOutputSchema,
  enrichmentRunInputSchema,
  enrichmentRunOutputSchema,
} from './schemas/enrichment.ts';
import {
  eventCreateInputSchema,
  eventGetInputSchema,
  eventListInputSchema,
  eventSchema,
  eventsCurrentInputSchema,
  eventsCurrentOutputSchema,
} from './schemas/events.ts';
import { healthPingOutputSchema } from './schemas/health.ts';
import {
  digestGetInputSchema,
  digestRegenerateInputSchema,
  digestRegenerateOutputSchema,
  digestSchema,
  digestTodayInputSchema,
  draftIdInputSchema,
  draftMarkSentInputSchema,
  draftRegenerateInputSchema,
  draftSchema,
  draftSendEmailInputSchema,
  draftSendEmailOutputSchema,
  draftUpdateInputSchema,
  followUpDismissInputSchema,
  followUpIdInputSchema,
  followUpSchema,
  followUpSnoozeInputSchema,
  followUpsListInputSchema,
  followUpsListOutputSchema,
  hermesFeedbackInputSchema,
  hermesFeedbackOutputSchema,
  hermesMemoriesListInputSchema,
  hermesMemoriesListOutputSchema,
  hermesMemoryDeactivateInputSchema,
  hermesMemorySchema,
  hermesProfileSchema,
  hermesProfileUpdateInputSchema,
} from './schemas/hermes.ts';
import {
  interactionCreateNoteInputSchema,
  interactionDeleteNoteInputSchema,
  interactionDeleteNoteOutputSchema,
  interactionFeedInputSchema,
  interactionFeedItemSchema,
  interactionSchema,
  interactionTimelineInputSchema,
  interactionUpdateNoteInputSchema,
} from './schemas/interactions.ts';
import {
  listCreateInputSchema,
  listDeleteInputSchema,
  listDeleteOutputSchema,
  listDetailSchema,
  listGetInputSchema,
  listItemsAddInputSchema,
  listItemsMutationOutputSchema,
  listItemsRemoveInputSchema,
  listListInputSchema,
  listSchema,
  listUpdateInputSchema,
} from './schemas/lists.ts';
import {
  meetingSchema,
  meetingsDiscardInputSchema,
  meetingsFinishInputSchema,
  meetingsFinishOutputSchema,
  meetingsHighlightsListInputSchema,
  meetingsHighlightsListOutputSchema,
  meetingsSpeakersAssignInputSchema,
  meetingsSpeakersAssignOutputSchema,
  meetingsSpeakersListInputSchema,
  meetingsSpeakersListOutputSchema,
  meetingsStartInputSchema,
  meetingsStartOutputSchema,
  meetingsSummaryGetInputSchema,
  meetingsSummaryGetOutputSchema,
  meetingsSummaryRegenerateInputSchema,
  meetingsSummaryRegenerateOutputSchema,
  meetingsTranscriptGetInputSchema,
  meetingsTranscriptGetOutputSchema,
  meetingsTranscriptSubmitInputSchema,
  meetingsTranscriptSubmitOutputSchema,
  meetingsUpcomingInputSchema,
  meetingsUpcomingOutputSchema,
} from './schemas/meetings.ts';
import {
  messageBodyInputSchema,
  messageBodySchema,
  messageThreadGetInputSchema,
  messageThreadMessagesInputSchema,
  messageThreadMessagesOutputSchema,
  messageThreadSchema,
  messageThreadsListInputSchema,
  messageThreadsListOutputSchema,
  syncMailToggleInputSchema,
  syncMailToggleOutputSchema,
} from './schemas/messages.ts';
import { llmSpendInputSchema, llmSpendOutputSchema } from './schemas/ops.ts';
import {
  organizationSchema,
  orgCreateInputSchema,
  orgGetInputSchema,
  orgInvitationCreateInputSchema,
  orgInvitationCreateOutputSchema,
  orgInvitationListInputSchema,
  orgInvitationListOutputSchema,
  orgInvitationRevokeInputSchema,
  orgInvitationSchema,
  orgListInputSchema,
  orgListOutputSchema,
  orgSettingsGetInputSchema,
  orgSettingsSchema,
  orgSettingsUpdateInputSchema,
  orgSwitchActiveInputSchema,
  orgSwitchActiveOutputSchema,
} from './schemas/org.ts';
import {
  contactSuppressionSchema,
  privacyDeleteConfirmInputSchema,
  privacyDeleteConfirmOutputSchema,
  privacyDeleteRequestInputSchema,
  privacyDeleteRequestOutputSchema,
  privacyExportDownloadInputSchema,
  privacyExportDownloadOutputSchema,
  privacyExportInputSchema,
  privacyExportOutputSchema,
  suppressionsCreateInputSchema,
  suppressionsListInputSchema,
  suppressionsListOutputSchema,
  suppressionsRemoveInputSchema,
  suppressionsRemoveOutputSchema,
} from './schemas/privacy.ts';
import {
  reviewCountsInputSchema,
  reviewCountsSchema,
  reviewListInputSchema,
  reviewListOutputSchema,
  reviewResolveInputSchema,
  reviewResolveOutputSchema,
} from './schemas/review.ts';
import {
  contactShareSchema,
  incomingShareSchema,
  listShareSchema,
  shareAcceptInviteInputSchema,
  shareAcceptInviteOutputSchema,
  shareAdoptInputSchema,
  shareAdoptOutputSchema,
  shareContactCreateInputSchema,
  shareContactRevokeInputSchema,
  shareInviteInputSchema,
  shareInviteOutputSchema,
  shareListCreateInputSchema,
  shareListRevokeInputSchema,
  shareRevokeOutputSchema,
  shareSummarySchema,
  sharesIncomingInputSchema,
  sharesOutgoingInputSchema,
} from './schemas/shares.ts';
import {
  connectedCalendarSchema,
  syncAccountDisconnectInputSchema,
  syncAccountDisconnectOutputSchema,
  syncAccountsListInputSchema,
  syncAccountsListOutputSchema,
  syncCalendarsListInputSchema,
  syncCalendarsListOutputSchema,
  syncCalendarToggleInputSchema,
  syncConnectUrlInputSchema,
  syncConnectUrlOutputSchema,
  syncStatusInputSchema,
  syncStatusOutputSchema,
} from './schemas/sync.ts';

/**
 * The type-safe error surface every procedure inherits. Keys are the oRPC error codes
 * (`ERROR_DEFS[code].orpcCode`); `data` is the `AppError` body, which the transport
 * expands into RFC 7807 problem+json.
 */
export const CONTRACT_ERROR_MAP = {
  UNAUTHORIZED: { status: 401, message: 'Unauthorized', data: appErrorSchema },
  FORBIDDEN: { status: 403, message: 'Forbidden', data: appErrorSchema },
  NOT_FOUND: { status: 404, message: 'Not Found', data: appErrorSchema },
  VALIDATION_FAILED: { status: 422, message: 'Validation Failed', data: appErrorSchema },
  CONFLICT: { status: 409, message: 'Conflict', data: appErrorSchema },
  IDEMPOTENCY_REPLAY: { status: 409, message: 'Idempotency Key Reused', data: appErrorSchema },
  BUDGET_EXHAUSTED: { status: 402, message: 'Budget Exhausted', data: appErrorSchema },
  RATE_LIMITED: { status: 429, message: 'Rate Limited', data: appErrorSchema },
  DEVICE_REVOKED: { status: 401, message: 'Device Revoked', data: appErrorSchema },
  SHARE_SCOPE_VIOLATION: { status: 403, message: 'Share Scope Violation', data: appErrorSchema },
  INTERNAL_SERVER_ERROR: { status: 500, message: 'Internal Server Error', data: appErrorSchema },
};

/** OpenAPI tag names, one per procedure group. */
export const CONTRACT_TAGS = [
  'health',
  'org',
  'contacts',
  'lists',
  'shares',
  'events',
  'interactions',
  'devices',
  'sync',
  'meetings',
  'enrichment',
  'commitments',
  'review',
  'messages',
  'ops',
  'hermes',
  'followUps',
  'drafts',
  'privacy',
] as const;

export type ContractTag = (typeof CONTRACT_TAGS)[number];

const base = oc.errors(CONTRACT_ERROR_MAP);

function op(
  method: HTTPMethod,
  path: HTTPPath,
  operationId: string,
  tag: ContractTag,
  summary: string,
) {
  return base.route({ method, path, operationId, summary, tags: [tag] });
}

// --- health ------------------------------------------------------------------

const health = {
  ping: op(
    'GET',
    '/health/ping',
    'healthPing',
    'health',
    'Liveness probe and server clock echo',
  ).output(healthPingOutputSchema),
};

// --- org ---------------------------------------------------------------------

const org = {
  list: op('GET', '/orgs', 'orgList', 'org', 'List the orgs the caller belongs to')
    .input(orgListInputSchema)
    .output(orgListOutputSchema),

  get: op('GET', '/orgs/{orgId}', 'orgGet', 'org', 'Fetch one org')
    .input(orgGetInputSchema)
    .output(organizationSchema),

  create: op('POST', '/orgs', 'orgCreate', 'org', 'Create an org (tenant)')
    .input(orgCreateInputSchema)
    .output(organizationSchema),

  settings: {
    get: op('GET', '/orgs/{orgId}/settings', 'orgSettingsGet', 'org', 'Fetch org settings')
      .input(orgSettingsGetInputSchema)
      .output(orgSettingsSchema),

    update: op('PATCH', '/orgs/{orgId}/settings', 'orgSettingsUpdate', 'org', 'Patch org settings')
      .input(orgSettingsUpdateInputSchema)
      .output(orgSettingsSchema),
  },

  switchActive: op(
    'POST',
    '/orgs/{orgId}/switch-active',
    'orgSwitchActive',
    'org',
    'Set the active org for the calling session',
  )
    .input(orgSwitchActiveInputSchema)
    .output(orgSwitchActiveOutputSchema),

  /**
   * THE INVITE-ONLY GATE (docs/PLAN.md: "No self-serve signup — owner
   * provisions"). An owner mints an invitation; the invitee redeems the token at
   * `POST /invitations/redeem`.
   *
   * That redemption is deliberately NOT a procedure here. It is unauthenticated
   * BY CONSTRUCTION — the redeemer has no account yet — and every procedure in
   * this contract sits behind the `authed` middleware, so modelling it as one
   * would mean an authenticated endpoint that must work without a session. Same
   * shape and same reason as `POST /devices/pair`.
   *
   * `list` is declared before the `{invitationId}` write for the same
   * static-vs-dynamic-segment readability reason as `contacts.matchKeys`.
   */
  invitations: {
    create: op(
      'POST',
      '/orgs/{orgId}/invitations',
      'orgInvitationsCreate',
      'org',
      'Mint a single-use, expiring invitation to join this organization',
    )
      .input(orgInvitationCreateInputSchema)
      .output(orgInvitationCreateOutputSchema),

    list: op(
      'GET',
      '/orgs/{orgId}/invitations',
      'orgInvitationsList',
      'org',
      'Invitations this organization has issued',
    )
      .input(orgInvitationListInputSchema)
      .output(orgInvitationListOutputSchema),

    /** A state change (`revoked_at`), not a row delete, so it is a POST. */
    revoke: op(
      'POST',
      '/orgs/{orgId}/invitations/{invitationId}/revoke',
      'orgInvitationsRevoke',
      'org',
      'Withdraw an invitation that has not been redeemed',
    )
      .input(orgInvitationRevokeInputSchema)
      .output(orgInvitationSchema),
  },
};

// --- contacts ----------------------------------------------------------------

const contacts = {
  list: op('GET', '/contacts', 'contactsList', 'contacts', 'List contacts (cursor paginated)')
    .input(contactListInputSchema)
    .output(contactListOutputSchema),

  /**
   * Declared before `contacts.get` so the static segment reads unambiguously;
   * oRPC matches static segments ahead of `{id}` regardless of declaration order
   * (the same note as `events.current`).
   *
   * GET with a `version` query param rather than a real `If-None-Match`: oRPC's
   * transport is a JSON envelope and has no conditional-GET affordance, so the
   * 304 is modelled in band. See `contactMatchKeysInputSchema`.
   */
  matchKeys: op(
    'GET',
    '/contacts/match-keys',
    'contactsMatchKeys',
    'contacts',
    'Normalized identity-key bundle for every contact (device-side matching)',
  )
    .input(contactMatchKeysInputSchema)
    .output(contactMatchKeysOutputSchema),

  get: op('GET', '/contacts/{id}', 'contactsGet', 'contacts', 'Fetch one contact with access')
    .input(contactGetInputSchema)
    .output(contactDetailSchema),

  create: op('POST', '/contacts', 'contactsCreate', 'contacts', 'Create a contact')
    .input(contactCreateInputSchema)
    .output(contactDetailSchema),

  update: op('PATCH', '/contacts/{id}', 'contactsUpdate', 'contacts', 'Patch a contact')
    .input(contactUpdateInputSchema)
    .output(contactDetailSchema),

  delete: op('DELETE', '/contacts/{id}', 'contactsDelete', 'contacts', 'Soft-delete a contact')
    .input(contactDeleteInputSchema)
    .output(contactDeleteOutputSchema),

  restore: op(
    'POST',
    '/contacts/{id}/restore',
    'contactsRestore',
    'contacts',
    'Restore a soft-deleted contact',
  )
    .input(contactRestoreInputSchema)
    .output(contactSchema),

  emails: {
    add: op(
      'POST',
      '/contacts/{contactId}/emails',
      'contactsEmailsAdd',
      'contacts',
      'Add an email address to a contact',
    )
      .input(contactEmailAddInputSchema)
      .output(contactEmailSchema),

    remove: op(
      'DELETE',
      '/contacts/{contactId}/emails/{emailId}',
      'contactsEmailsRemove',
      'contacts',
      'Remove an email address from a contact',
    )
      .input(contactEmailRemoveInputSchema)
      .output(okSchema),
  },

  phones: {
    add: op(
      'POST',
      '/contacts/{contactId}/phones',
      'contactsPhonesAdd',
      'contacts',
      'Add a phone number to a contact',
    )
      .input(contactPhoneAddInputSchema)
      .output(contactPhoneSchema),

    remove: op(
      'DELETE',
      '/contacts/{contactId}/phones/{phoneId}',
      'contactsPhonesRemove',
      'contacts',
      'Remove a phone number from a contact',
    )
      .input(contactPhoneRemoveInputSchema)
      .output(okSchema),
  },

  /**
   * The per-contact refusals: do-not-record, no-enrichment, no-outreach
   * (design §1.4 / §4.7).
   *
   * `remove` is a POST, not a DELETE, and that is not a style choice: lifting a
   * flag CLEARS it and keeps the row whenever a GDPR key hash is still there to
   * remember the erasure by, so "delete" would promise a disappearance the
   * server must not perform. The per-flag payload also cannot ride on a DELETE,
   * which this contract restricts to path params.
   *
   * `list` is declared before the `{contactId}` writes for the same static-vs-
   * dynamic-segment readability reason as `contacts.matchKeys`.
   */
  suppressions: {
    list: op(
      'GET',
      '/contacts/suppressions',
      'contactsSuppressionsList',
      'contacts',
      'Contacts carrying a do-not-record, no-enrichment or no-outreach flag',
    )
      .input(suppressionsListInputSchema)
      .output(suppressionsListOutputSchema),

    create: op(
      'POST',
      '/contacts/{contactId}/suppressions',
      'contactsSuppressionsCreate',
      'contacts',
      'Set suppression flags on a contact (absent flags are untouched)',
    )
      .input(suppressionsCreateInputSchema)
      .output(contactSuppressionSchema),

    remove: op(
      'POST',
      '/contacts/{contactId}/suppressions/remove',
      'contactsSuppressionsRemove',
      'contacts',
      'Clear suppression flags; an erasure tombstone survives the clear',
    )
      .input(suppressionsRemoveInputSchema)
      .output(suppressionsRemoveOutputSchema),
  },
};

// --- lists -------------------------------------------------------------------

const lists = {
  list: op('GET', '/lists', 'listsList', 'lists', 'List contact lists')
    .input(listListInputSchema)
    .output(paginated(listSchema)),

  get: op('GET', '/lists/{id}', 'listsGet', 'lists', 'Fetch a list with its first page of members')
    .input(listGetInputSchema)
    .output(listDetailSchema),

  create: op('POST', '/lists', 'listsCreate', 'lists', 'Create a contact list')
    .input(listCreateInputSchema)
    .output(listSchema),

  update: op('PATCH', '/lists/{id}', 'listsUpdate', 'lists', 'Patch a contact list')
    .input(listUpdateInputSchema)
    .output(listSchema),

  delete: op('DELETE', '/lists/{id}', 'listsDelete', 'lists', 'Soft-delete a contact list')
    .input(listDeleteInputSchema)
    .output(listDeleteOutputSchema),

  items: {
    add: op(
      'POST',
      '/lists/{listId}/items',
      'listsItemsAdd',
      'lists',
      'Add contacts to a list (bulk)',
    )
      .input(listItemsAddInputSchema)
      .output(listItemsMutationOutputSchema),

    /**
     * POST rather than DELETE: the payload is a bulk id array, and a request body on
     * DELETE is unreliable across the four client HTTP stacks.
     */
    remove: op(
      'POST',
      '/lists/{listId}/items/remove',
      'listsItemsRemove',
      'lists',
      'Remove contacts from a list (bulk)',
    )
      .input(listItemsRemoveInputSchema)
      .output(listItemsMutationOutputSchema),
  },
};

// --- shares ------------------------------------------------------------------

const shares = {
  contact: {
    create: op(
      'POST',
      '/shares/contacts',
      'sharesContactCreate',
      'shares',
      'Grant read access to a contact',
    )
      .input(shareContactCreateInputSchema)
      .output(contactShareSchema),

    /** Revocation is a state change (`revoked_at`), not a row delete, so it is a POST. */
    revoke: op(
      'POST',
      '/shares/contacts/{shareId}/revoke',
      'sharesContactRevoke',
      'shares',
      'Revoke a contact grant',
    )
      .input(shareContactRevokeInputSchema)
      .output(shareRevokeOutputSchema),
  },

  list: {
    create: op('POST', '/shares/lists', 'sharesListCreate', 'shares', 'Grant read access to a list')
      .input(shareListCreateInputSchema)
      .output(listShareSchema),

    revoke: op(
      'POST',
      '/shares/lists/{shareId}/revoke',
      'sharesListRevoke',
      'shares',
      'Revoke a list grant',
    )
      .input(shareListRevokeInputSchema)
      .output(shareRevokeOutputSchema),
  },

  outgoing: op('GET', '/shares/outgoing', 'sharesOutgoing', 'shares', 'Grants this org has made')
    .input(sharesOutgoingInputSchema)
    .output(paginated(shareSummarySchema)),

  incoming: op(
    'GET',
    '/shares/incoming',
    'sharesIncoming',
    'shares',
    'Grants made to this org or caller',
  )
    .input(sharesIncomingInputSchema)
    .output(paginated(incomingShareSchema)),

  adopt: op(
    'POST',
    '/shares/{shareId}/adopt',
    'sharesAdopt',
    'shares',
    'Fork a shared contact into this org',
  )
    .input(shareAdoptInputSchema)
    .output(shareAdoptOutputSchema),

  /**
   * Invite someone who is not a user yet (§1.5's `share_invitations`).
   *
   * Declared before `acceptInvite` because that is the order they happen in, and
   * both sit under `/shares/invitations` rather than `/shares/invite` so the
   * resource reads as a noun.
   *
   * The response carries the plaintext token EXACTLY ONCE — only its sha256 is
   * stored, exactly like the device-pairing key. There is no procedure that can
   * show it again; a lost link is re-invited, not recovered.
   */
  invite: op(
    'POST',
    '/shares/invitations',
    'sharesInvite',
    'shares',
    'Mint a single-use, expiring invitation to a contact or list',
  )
    .input(shareInviteInputSchema)
    .output(shareInviteOutputSchema),

  /**
   * Redeem an invitation. AUTHENTICATED: the token says which grant to make, the
   * session says who is receiving it — an invitation binds to the accepting
   * user's ACTIVE ORG, because a grant to a person with no tenant has nowhere to
   * land and nothing to read it with.
   */
  acceptInvite: op(
    'POST',
    '/shares/invitations/accept',
    'sharesAcceptInvite',
    'shares',
    'Redeem an invitation token, binding the grant to the caller active org',
  )
    .input(shareAcceptInviteInputSchema)
    .output(shareAcceptInviteOutputSchema),
};

// --- events ------------------------------------------------------------------

const events = {
  list: op('GET', '/events', 'eventsList', 'events', 'List CRM events')
    .input(eventListInputSchema)
    .output(paginated(eventSchema)),

  /**
   * Declared before `events.get` so the static segment is unambiguous to readers;
   * oRPC's router matches static segments ahead of `{id}` regardless of order.
   */
  current: op(
    'GET',
    '/events/current',
    'eventsCurrent',
    'events',
    'Ranked "you are probably here" candidates for the met-at field',
  )
    .input(eventsCurrentInputSchema)
    .output(eventsCurrentOutputSchema),

  get: op('GET', '/events/{id}', 'eventsGet', 'events', 'Fetch one CRM event')
    .input(eventGetInputSchema)
    .output(eventSchema),

  create: op('POST', '/events', 'eventsCreate', 'events', 'Create a CRM event')
    .input(eventCreateInputSchema)
    .output(eventSchema),
};

// --- interactions ------------------------------------------------------------

const interactions = {
  timeline: op(
    'GET',
    '/contacts/{contactId}/interactions',
    'interactionsTimeline',
    'interactions',
    'Reverse-chronological timeline for one contact',
  )
    .input(interactionTimelineInputSchema)
    .output(paginated(interactionSchema)),

  createNote: op(
    'POST',
    '/contacts/{contactId}/notes',
    'interactionsCreateNote',
    'interactions',
    'Log a manual note against a contact',
  )
    .input(interactionCreateNoteInputSchema)
    .output(interactionSchema),

  updateNote: op(
    'PATCH',
    '/interactions/notes/{id}',
    'interactionsUpdateNote',
    'interactions',
    'Edit one note block (notes only — synced interactions are immutable)',
  )
    .input(interactionUpdateNoteInputSchema)
    .output(interactionSchema),

  deleteNote: op(
    'DELETE',
    '/interactions/notes/{id}',
    'interactionsDeleteNote',
    'interactions',
    'Remove one note block (soft delete; notes only)',
  )
    .input(interactionDeleteNoteInputSchema)
    .output(interactionDeleteNoteOutputSchema),

  feed: op(
    'GET',
    '/interactions/feed',
    'interactionsFeed',
    'interactions',
    'Org-wide recent interaction feed',
  )
    .input(interactionFeedInputSchema)
    .output(paginated(interactionFeedItemSchema)),
};

// --- the contract ------------------------------------------------------------

// --- devices -----------------------------------------------------------------

const devices = {
  createPairingCode: op(
    'POST',
    '/devices/pairing-codes',
    'deviceCreatePairingCode',
    'devices',
    'Mint a single-use 5-minute pairing code for a new device',
  )
    .input(deviceCreatePairingCodeInputSchema)
    .output(devicePairingCodeSchema),

  list: op('GET', '/devices', 'deviceList', 'devices', 'List the caller paired devices').output(
    deviceListOutputSchema,
  ),

  revoke: op(
    'POST',
    '/devices/{deviceId}/revoke',
    'deviceRevoke',
    'devices',
    'Revoke a paired device (its API key dies with it)',
  )
    .input(deviceRevokeInputSchema)
    .output(deviceSchema),

  /**
   * The Expo push registration (§1.14). No `deviceId`: the token is written onto
   * the CALLER'S device row, which its API key already identifies — see
   * `devicePushTokenUpdateInputSchema` for why naming a device would be a
   * notification-hijacking primitive.
   */
  updatePushToken: op(
    'POST',
    '/devices/push-token',
    'deviceUpdatePushToken',
    'devices',
    'Register (or clear) the calling device Expo push token',
  )
    .input(devicePushTokenUpdateInputSchema)
    .output(devicePushTokenSchema),
};

// --- sync --------------------------------------------------------------------

/**
 * Connected provider accounts. Note what is NOT here: the OAuth callback itself.
 * `/connect/:provider/callback` is a raw Hono route because a provider redirects
 * a BROWSER to it with query params and expects a 302 back — oRPC's JSON
 * envelope cannot express that. `connectUrl` is the oRPC half: it mints the
 * signed state and hands the SPA a URL to navigate to.
 */
const sync = {
  accounts: {
    list: op(
      'GET',
      '/sync/accounts',
      'syncAccountsList',
      'sync',
      'List connected provider accounts',
    )
      .input(syncAccountsListInputSchema)
      .output(syncAccountsListOutputSchema),

    /**
     * POST rather than GET: it MINTS a single-use signed state, which is a
     * side effect, and a GET would let a browser prefetch burn one.
     */
    connectUrl: op(
      'POST',
      '/sync/accounts/connect-url',
      'syncAccountsConnectUrl',
      'sync',
      'Mint a provider OAuth authorization URL with a signed state',
    )
      .input(syncConnectUrlInputSchema)
      .output(syncConnectUrlOutputSchema),

    disconnect: op(
      'POST',
      '/sync/accounts/{accountId}/disconnect',
      'syncAccountsDisconnect',
      'sync',
      'Revoke an account credentials, keeping its synced history',
    )
      .input(syncAccountDisconnectInputSchema)
      .output(syncAccountDisconnectOutputSchema),

    /**
     * POST rather than a field on a generic account PATCH: ENABLING has a side
     * effect well beyond the flag — it arms provider push and starts a
     * twelve-month bounded backfill — and hiding that behind a checkbox update
     * would make a history walk look like a preference change.
     */
    mailToggle: op(
      'POST',
      '/sync/accounts/{accountId}/mail',
      'syncAccountsMailToggle',
      'sync',
      'Turn mail sync on or off for one account (on also arms push and backfills)',
    )
      .input(syncMailToggleInputSchema)
      .output(syncMailToggleOutputSchema),
  },

  status: op(
    'GET',
    '/sync/status',
    'syncStatus',
    'sync',
    'Watermark ages and subscription expiries per account (the ops dashboard)',
  )
    .input(syncStatusInputSchema)
    .output(syncStatusOutputSchema),

  calendars: {
    list: op(
      'GET',
      '/sync/accounts/{accountId}/calendars',
      'syncCalendarsList',
      'sync',
      'List an account calendars from the local mirror',
    )
      .input(syncCalendarsListInputSchema)
      .output(syncCalendarsListOutputSchema),

    toggle: op(
      'POST',
      '/sync/accounts/{accountId}/calendars/toggle',
      'syncCalendarsToggle',
      'sync',
      'Turn syncing on or off for one calendar',
    )
      .input(syncCalendarToggleInputSchema)
      .output(connectedCalendarSchema),
  },
};

// --- meetings ----------------------------------------------------------------

/**
 * Design-clients §4.3-§4.5 and design-intelligence §3.1.
 *
 * Two shapes are load-bearing rather than stylistic:
 *
 *  - **`start` never errors for a refused meeting.** A do-not-record attendee is
 *    an expected answer, and §4.7 requires the Mac to say WHY it will not arm. A
 *    403 arrives through the client's retry layer indistinguishable from a
 *    network failure, so the refusal is a FIELD on a 200.
 *  - **`summary.regenerate` ENQUEUES.** A regenerate is a TOP-tier call over a
 *    whole transcript; a procedure that rebuilt inline would let a double-tap, a
 *    retry or a prefetch pay for it twice. Same reasoning as
 *    `hermes.digest.regenerate`.
 *
 * `/meetings/upcoming` is declared before `/meetings/{meetingId}/...` so the
 * static segment reads unambiguously; oRPC matches static segments ahead of
 * `{id}` regardless of declaration order (the same note as `events.current`).
 */
const meetings = {
  upcoming: op(
    'GET',
    '/meetings/upcoming',
    'meetingsUpcoming',
    'meetings',
    'Recordable meetings in the next window, attendees pre-resolved (recorder feed)',
  )
    .input(meetingsUpcomingInputSchema)
    .output(meetingsUpcomingOutputSchema),

  start: op(
    'POST',
    '/meetings/start',
    'meetingsStart',
    'meetings',
    'Open a meeting for a device recording, or report why it is refused',
  )
    .input(meetingsStartInputSchema)
    .output(meetingsStartOutputSchema),

  finish: op(
    'POST',
    '/meetings/{meetingId}/finish',
    'meetingsFinish',
    'meetings',
    'Close a recording and register its tracks (the RMS silent-tap guard runs here)',
  )
    .input(meetingsFinishInputSchema)
    .output(meetingsFinishOutputSchema),

  discard: op(
    'POST',
    '/meetings/{meetingId}/discard',
    'meetingsDiscard',
    'meetings',
    'Throw a recording away (a state change, so the refusal stays explainable)',
  )
    .input(meetingsDiscardInputSchema)
    .output(meetingSchema),

  transcript: {
    submit: op(
      'POST',
      '/meetings/{meetingId}/transcript',
      'meetingsTranscriptSubmit',
      'meetings',
      'Upload a chunk of merged transcript segments (<=500, idempotent by index)',
    )
      .input(meetingsTranscriptSubmitInputSchema)
      .output(meetingsTranscriptSubmitOutputSchema),

    get: op(
      'GET',
      '/meetings/{meetingId}/transcript',
      'meetingsTranscriptGet',
      'meetings',
      'The speaker-labelled transcript, paged by absolute segment index',
    )
      .input(meetingsTranscriptGetInputSchema)
      .output(meetingsTranscriptGetOutputSchema),
  },

  summary: {
    get: op(
      'GET',
      '/meetings/{meetingId}/summary',
      'meetingsSummaryGet',
      'meetings',
      'The current detailed summary for a meeting',
    )
      .input(meetingsSummaryGetInputSchema)
      .output(meetingsSummaryGetOutputSchema),

    regenerate: op(
      'POST',
      '/meetings/{meetingId}/summary/regenerate',
      'meetingsSummaryRegenerate',
      'meetings',
      'Queue a rebuild of the digest for one meeting (the run happens on the worker)',
    )
      .input(meetingsSummaryRegenerateInputSchema)
      .output(meetingsSummaryRegenerateOutputSchema),
  },

  highlights: {
    list: op(
      'GET',
      '/meetings/{meetingId}/highlights',
      'meetingsHighlightsList',
      'meetings',
      'Decisions, action items and key points, each citing a transcript segment',
    )
      .input(meetingsHighlightsListInputSchema)
      .output(meetingsHighlightsListOutputSchema),
  },

  speakers: {
    list: op(
      'GET',
      '/meetings/{meetingId}/speakers',
      'meetingsSpeakersList',
      'meetings',
      'Attributed voices plus the candidate set the assignment UI may choose from',
    )
      .input(meetingsSpeakersListInputSchema)
      .output(meetingsSpeakersListOutputSchema),

    assign: op(
      'POST',
      '/meetings/{meetingId}/speakers/{speakerId}/assign',
      'meetingsSpeakersAssign',
      'meetings',
      'Name a voice, optionally teaching the contact voiceprint (a human decision)',
    )
      .input(meetingsSpeakersAssignInputSchema)
      .output(meetingsSpeakersAssignOutputSchema),
  },
};

// --- enrichment --------------------------------------------------------------

/**
 * Design §3.2. Two shapes here are load-bearing rather than stylistic:
 *
 *  - `run` is a POST that DOES NOT SPEND unless `confirm: true`. The unconfirmed
 *    call is how the UI obtains the cost estimate it is required to show, so the
 *    "explicit opt-in + shown cost estimate" gate is enforced by the contract
 *    rather than by a UI convention someone can forget.
 *  - **there is no `findPhone`.** Mobile lookup is 8-10 credits against 1 for an
 *    email; the design makes it an explicit costed button and Wave 1 does not
 *    expose it at all, so it cannot be reached by accident.
 */
const enrichment = {
  run: op(
    'POST',
    '/enrichment/run',
    'enrichmentRun',
    'enrichment',
    'Estimate (and, with confirm, start) an enrichment run for one contact',
  )
    .input(enrichmentRunInputSchema)
    .output(enrichmentRunOutputSchema),

  identify: op(
    'POST',
    '/enrichment/identify',
    'enrichmentIdentify',
    'enrichment',
    'Ranked identity candidates for an ambiguous person (never auto-accepted)',
  )
    .input(enrichmentIdentifyInputSchema)
    .output(enrichmentIdentifyOutputSchema),

  applyIdentity: op(
    'POST',
    '/enrichment/identity/apply',
    'enrichmentApplyIdentity',
    'enrichment',
    "Apply the identify candidate the user picked — 'this is them' — through the merge layer",
  )
    .input(enrichmentApplyIdentityInputSchema)
    .output(enrichmentApplyIdentityOutputSchema),

  candidates: {
    list: op(
      'GET',
      '/enrichment/candidates',
      'enrichmentCandidatesList',
      'enrichment',
      'Field values enrichment has proposed, awaiting a decision',
    )
      .input(enrichmentCandidatesListInputSchema)
      .output(enrichmentCandidatesListOutputSchema),

    accept: op(
      'POST',
      '/enrichment/candidates/{candidateId}/accept',
      'enrichmentCandidatesAccept',
      'enrichment',
      'Apply a proposed field value through the merge layer',
    )
      .input(enrichmentCandidateDecisionInputSchema)
      .output(enrichmentCandidateDecisionOutputSchema),

    reject: op(
      'POST',
      '/enrichment/candidates/{candidateId}/reject',
      'enrichmentCandidatesReject',
      'enrichment',
      'Reject a proposed field value, with a reason the queue remembers',
    )
      .input(enrichmentCandidateRejectInputSchema)
      .output(enrichmentCandidateDecisionOutputSchema),
  },

  history: op(
    'GET',
    '/enrichment/history',
    'enrichmentHistory',
    'enrichment',
    'Append-only enrichment runs (what we asked, what it cost)',
  )
    .input(enrichmentHistoryInputSchema)
    .output(enrichmentHistoryOutputSchema),

  budget: op(
    'GET',
    '/enrichment/budget',
    'enrichmentBudget',
    'enrichment',
    'Vendor credit usage against the monthly cap, per provider',
  )
    .input(enrichmentBudgetInputSchema)
    .output(enrichmentBudgetOutputSchema),
};

// --- commitments -------------------------------------------------------------

/** The "Promises" view (design §1.10). Filterable by direction, status and age. */
const commitments = {
  list: op(
    'GET',
    '/commitments',
    'commitmentsList',
    'commitments',
    'Promises in both directions, with their verbatim evidence',
  )
    .input(commitmentsListInputSchema)
    .output(commitmentsListOutputSchema),

  update: op(
    'PATCH',
    '/commitments/{commitmentId}',
    'commitmentsUpdate',
    'commitments',
    'Move a commitment through its state machine',
  )
    .input(commitmentUpdateInputSchema)
    .output(commitmentSchema),

  /**
   * Confirming is what lifts a `needs_review` row (mined below the 0.6
   * confidence threshold) into the drafting pool, so it is a POST of its own
   * rather than a status PATCH: the two mean different things to the digest.
   */
  confirm: op(
    'POST',
    '/commitments/{commitmentId}/confirm',
    'commitmentsConfirm',
    'commitments',
    'Confirm a mined commitment is real, promoting it out of review',
  )
    .input(commitmentConfirmInputSchema)
    .output(commitmentSchema),
};

// --- review ------------------------------------------------------------------

/**
 * The unified confirmation queue (design §2.8). Three tables, one feed, one
 * keyboard model — see `schemas/review.ts` for why the merge is server-side and
 * why dismissal is keyed by content hash rather than row id.
 */
const review = {
  list: op(
    'GET',
    '/review',
    'reviewList',
    'review',
    'Everything the system guessed and a human confirms, newest first',
  )
    .input(reviewListInputSchema)
    .output(reviewListOutputSchema),

  /**
   * POST, and one procedure rather than accept/dismiss pairs: the queue's whole
   * interaction model is "one key, one decision", and a single call site is what
   * guarantees a dismissal always writes its tombstone.
   */
  resolve: op(
    'POST',
    '/review/resolve',
    'reviewResolve',
    'review',
    'Accept or dismiss one queue item (dismissal writes a content-hash tombstone)',
  )
    .input(reviewResolveInputSchema)
    .output(reviewResolveOutputSchema),

  counts: op('GET', '/review/counts', 'reviewCounts', 'review', 'Queue depth by source')
    .input(reviewCountsInputSchema)
    .output(reviewCountsSchema),
};

// --- messages ----------------------------------------------------------------

/**
 * The email lane's read surface (design §1.8 / §3.3).
 *
 * What is NOT here is the point: there is no "list my mailbox". Only threads
 * with a CRM contact on them exist in the database at all — the retention rule
 * — so every read is anchored to a contact or to a thread we already kept.
 *
 * `body` is its own procedure rather than a field, because it is the one read
 * that could leak a private body across a share scope, and because a
 * forty-message thread's bodies are megabytes that live in S3.
 */
const messages = {
  threads: {
    list: op(
      'GET',
      '/messages/threads',
      'messagesThreadsList',
      'messages',
      'Email threads with CRM contacts, newest first',
    )
      .input(messageThreadsListInputSchema)
      .output(messageThreadsListOutputSchema),
  },

  thread: {
    get: op(
      'GET',
      '/messages/threads/{threadId}',
      'messagesThreadGet',
      'messages',
      'One thread with its summary and state',
    )
      .input(messageThreadGetInputSchema)
      .output(messageThreadSchema),

    messages: op(
      'GET',
      '/messages/threads/{threadId}/messages',
      'messagesThreadMessages',
      'messages',
      'Messages in a thread, oldest first',
    )
      .input(messageThreadMessagesInputSchema)
      .output(messageThreadMessagesOutputSchema),
  },

  /**
   * Streamed through the API, never presigned: a presigned URL is a bearer
   * credential for a private email body with no tenant check attached.
   */
  body: op(
    'GET',
    '/messages/{messageId}/body',
    'messagesBody',
    'messages',
    'The stored body of one message, streamed through the API',
  )
    .input(messageBodyInputSchema)
    .output(messageBodySchema),
};

// --- ops ---------------------------------------------------------------------

const ops = {
  llmSpend: op(
    'GET',
    '/ops/llm-spend',
    'opsLlmSpend',
    'ops',
    'LLM spend by task and tier, with the cache-hit ratio and budget posture',
  )
    .input(llmSpendInputSchema)
    .output(llmSpendOutputSchema),
};

// --- hermes ------------------------------------------------------------------

/**
 * Design §2.2 / §2.4. Three groups, split by what a client actually does with
 * them: `hermes` is the agent's own state and its daily output, `followUps` is
 * the queue a human works through, and `drafts` is the text they send.
 *
 * `hermes.digest.regenerate` is a POST that ENQUEUES rather than a GET that
 * rebuilds: a digest run makes two TOP-tier calls, and a procedure that did that
 * inline would let a retry, a double-tap or a prefetch pay for it twice.
 */
const hermes = {
  digest: {
    today: op(
      'GET',
      '/hermes/digest/today',
      'hermesDigestToday',
      'hermes',
      "Today's digest with its items, evidence and drafts (or the newest one)",
    )
      .input(digestTodayInputSchema)
      .output(digestSchema.nullable()),

    get: op('GET', '/hermes/digest/{digestId}', 'hermesDigestGet', 'hermes', 'Fetch one digest')
      .input(digestGetInputSchema)
      .output(digestSchema),

    regenerate: op(
      'POST',
      '/hermes/digest/regenerate',
      'hermesDigestRegenerate',
      'hermes',
      'Queue a rebuild of one local date (the run itself happens on the worker)',
    )
      .input(digestRegenerateInputSchema)
      .output(digestRegenerateOutputSchema),
  },

  profile: {
    get: op(
      'GET',
      '/hermes/profile',
      'hermesProfileGet',
      'hermes',
      'The L1 profile plus the current persona document',
    ).output(hermesProfileSchema),

    update: op('PATCH', '/hermes/profile', 'hermesProfileUpdate', 'hermes', 'Patch the L1 profile')
      .input(hermesProfileUpdateInputSchema)
      .output(hermesProfileSchema),
  },

  memories: {
    list: op(
      'GET',
      '/hermes/memories',
      'hermesMemoriesList',
      'hermes',
      'What Hermes has learned about you, with provenance',
    )
      .input(hermesMemoriesListInputSchema)
      .output(hermesMemoriesListOutputSchema),

    /**
     * The **Undo** §2.2 calls mandatory. A retraction, not a delete: the row
     * stays readable so provenance survives and a bad inference can be traced.
     */
    deactivate: op(
      'POST',
      '/hermes/memories/{memoryId}/deactivate',
      'hermesMemoriesDeactivate',
      'hermes',
      'Retract a learned memory (append-only: the row survives, superseded)',
    )
      .input(hermesMemoryDeactivateInputSchema)
      .output(hermesMemorySchema),
  },

  feedback: op(
    'POST',
    '/hermes/feedback',
    'hermesFeedback',
    'hermes',
    'Record an accept / edit / reject / snooze, feeding the nightly weight fold',
  )
    .input(hermesFeedbackInputSchema)
    .output(hermesFeedbackOutputSchema),

  /**
   * §2.3 — Hermes Chat. Five procedures, and the split is the confirmation rule:
   * `send` runs a bounded tool loop that may write only the two things §2.3
   * permits, and `confirm` / `cancel` are the human half of everything else.
   *
   * `send` is a POST that RUNS the turn rather than enqueuing it, unlike
   * `digest.regenerate`. The difference is the bound: a digest run is one
   * compose plus N drafts with a 90-second p95 and no user waiting, while a chat
   * turn is at most nine calls with a person watching. Enqueuing it would add a
   * delivery channel to save nothing.
   */
  chat: {
    send: op(
      'POST',
      '/hermes/chat/send',
      'hermesChatSend',
      'hermes',
      'Run one bounded tool-calling turn (max 8 tool calls) and return the reply',
    )
      .input(chatSendInputSchema)
      .output(chatSendOutputSchema),

    sessions: op(
      'GET',
      '/hermes/chat/sessions',
      'hermesChatSessions',
      'hermes',
      'The conversation list, newest first',
    )
      .input(chatSessionsInputSchema)
      .output(chatSessionsOutputSchema),

    history: op(
      'GET',
      '/hermes/chat/sessions/{sessionId}',
      'hermesChatHistory',
      'hermes',
      'One transcript with its tool traffic and its outstanding proposals',
    )
      .input(chatHistoryInputSchema)
      .output(chatHistoryOutputSchema),

    /**
     * The gate. Takes an ACTION ID and nothing else: what executes is the
     * payload the server stored when the model proposed it, so a client can
     * never confirm something other than what the user was shown.
     */
    confirm: op(
      'POST',
      '/hermes/chat/actions/{actionId}/confirm',
      'hermesChatConfirm',
      'hermes',
      'Execute a proposed action the user has approved',
    )
      .input(chatActionInputSchema)
      .output(chatActionOutputSchema),

    cancel: op(
      'POST',
      '/hermes/chat/actions/{actionId}/cancel',
      'hermesChatCancel',
      'hermes',
      'Decline a proposed action',
    )
      .input(chatActionInputSchema)
      .output(chatActionOutputSchema),
  },
};

// --- followUps ---------------------------------------------------------------

const followUps = {
  list: op(
    'GET',
    '/follow-ups',
    'followUpsList',
    'followUps',
    'The follow-up queue, with the evidence behind each item',
  )
    .input(followUpsListInputSchema)
    .output(followUpsListOutputSchema),

  accept: op(
    'POST',
    '/follow-ups/{followUpId}/accept',
    'followUpsAccept',
    'followUps',
    'Take an item on: it stays in the queue until it is sent or completed',
  )
    .input(followUpIdInputSchema)
    .output(followUpSchema),

  snooze: op(
    'POST',
    '/follow-ups/{followUpId}/snooze',
    'followUpsSnooze',
    'followUps',
    'Right person, wrong day. The tick revives it when the time comes',
  )
    .input(followUpSnoozeInputSchema)
    .output(followUpSchema),

  dismiss: op(
    'POST',
    '/follow-ups/{followUpId}/dismiss',
    'followUpsDismiss',
    'followUps',
    'Should not have been raised. The reason feeds the learning loop',
  )
    .input(followUpDismissInputSchema)
    .output(followUpSchema),

  complete: op(
    'POST',
    '/follow-ups/{followUpId}/complete',
    'followUpsComplete',
    'followUps',
    'Handled, by whatever means',
  )
    .input(followUpIdInputSchema)
    .output(followUpSchema),
};

// --- drafts ------------------------------------------------------------------

/**
 * §2.5's honest promise: "email is one tap; SMS is one tap inside Messages with
 * everything prefilled; LinkedIn is copy -> paste -> Enter."
 *
 * Which is why `sendEmail` exists and `sendSms` / `sendLinkedin` do not. SMS
 * from a server number "reads as spam and breaks the existing thread", and
 * LinkedIn has no member-to-member API at any partner tier — so `markCopied` and
 * `markSent` are the truthful surface for both.
 */
const drafts = {
  get: op('GET', '/drafts/{draftId}', 'draftsGet', 'drafts', 'Fetch one draft')
    .input(draftIdInputSchema)
    .output(draftSchema),

  update: op('PATCH', '/drafts/{draftId}', 'draftsUpdate', 'drafts', 'Edit a draft before sending')
    .input(draftUpdateInputSchema)
    .output(draftSchema),

  regenerate: op(
    'POST',
    '/drafts/{draftId}/regenerate',
    'draftsRegenerate',
    'drafts',
    'Rewrite one draft (ONE cheap call, never a full recompose)',
  )
    .input(draftRegenerateInputSchema)
    .output(draftSchema),

  markCopied: op(
    'POST',
    '/drafts/{draftId}/copied',
    'draftsMarkCopied',
    'drafts',
    'The LinkedIn path: the text is on the clipboard. Still a draft',
  )
    .input(draftIdInputSchema)
    .output(draftSchema),

  markSent: op(
    'POST',
    '/drafts/{draftId}/sent',
    'draftsMarkSent',
    'drafts',
    'The user sent it themselves (LinkedIn, SMS). Terminal',
  )
    .input(draftMarkSentInputSchema)
    .output(draftSchema),

  discard: op(
    'POST',
    '/drafts/{draftId}/discard',
    'draftsDiscard',
    'drafts',
    'Throw it away — and, inside the 30-second window, undo a dispatch',
  )
    .input(draftIdInputSchema)
    .output(draftSchema),

  /**
   * The ONE genuinely one-tap send (§2.5). Every safety rule is enforced here
   * and not only in the client: the caps, the INVALID hard block, quiet hours.
   */
  sendEmail: op(
    'POST',
    '/drafts/{draftId}/send-email',
    'draftsSendEmail',
    'drafts',
    'Send this draft from the user own mailbox, subject to every §2.5 rule',
  )
    .input(draftSendEmailInputSchema)
    .output(draftSendEmailOutputSchema),
};

// --- privacy -----------------------------------------------------------------

/**
 * GDPR Article 15 and Article 17, plus App Store guideline 5.1.1(v).
 *
 * Two shapes here are load-bearing rather than stylistic:
 *
 *  - **`export` returns an id, never a URL.** A presigned link is a bearer
 *    credential for a complete copy of a tenant's CRM with no tenant check
 *    attached; the bundle is streamed back through `exportDownload` under the
 *    same `ActorContext` as every other read. Identical reasoning to
 *    `messages.body`.
 *  - **deletion is two procedures.** An irreversible erasure behind one
 *    authenticated POST is one mis-tapped button or one replayed request away
 *    from destroying a tenant, and there is no undo.
 *
 * `contacts.suppressions.*` is the OTHER half of this file's schemas and lives
 * under the `contacts` group, because a per-contact refusal is a fact about a
 * contact; these four are facts about the account.
 */
const privacy = {
  export: op(
    'POST',
    '/privacy/exports',
    'privacyExport',
    'privacy',
    'Assemble this organization data as a JSON bundle and return its reference',
  )
    .input(privacyExportInputSchema)
    .output(privacyExportOutputSchema),

  exportDownload: op(
    'GET',
    '/privacy/exports/{exportId}',
    'privacyExportDownload',
    'privacy',
    'Stream a previously assembled bundle back through the API',
  )
    .input(privacyExportDownloadInputSchema)
    .output(privacyExportDownloadOutputSchema),

  deleteAccount: {
    request: op(
      'POST',
      '/privacy/delete-account',
      'privacyDeleteAccountRequest',
      'privacy',
      'Begin account deletion: returns a short-lived confirmation token',
    )
      .input(privacyDeleteRequestInputSchema)
      .output(privacyDeleteRequestOutputSchema),

    confirm: op(
      'POST',
      '/privacy/delete-account/confirm',
      'privacyDeleteAccountConfirm',
      'privacy',
      'Execute the erasure: tenant data goes, suppressions and the audit log stay',
    )
      .input(privacyDeleteConfirmInputSchema)
      .output(privacyDeleteConfirmOutputSchema),
  },
};

export const contract = {
  health,
  org,
  contacts,
  lists,
  shares,
  events,
  interactions,
  devices,
  sync,
  meetings,
  enrichment,
  commitments,
  review,
  messages,
  ops,
  hermes,
  followUps,
  drafts,
  privacy,
};

export type AppContract = typeof contract;
